import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

const inputClass =
  "w-full bg-ink border border-[rgba(176,132,51,0.3)] px-4 py-3 text-bone placeholder:text-bone/30 focus:outline-none focus:border-gold transition-colors";

function Card({ children }) {
  return (
    <div className="admin-area min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-sm border border-[rgba(176,132,51,0.3)] bg-surface/70 backdrop-blur-xl p-8 flex flex-col gap-5">
        <img src="/assets/logo-fs.png" alt="Full Stack" className="h-16 w-16 object-contain mx-auto" />
        {children}
      </div>
    </div>
  );
}

function RequestReset() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Card>
        <h1 className="font-display text-2xl tracking-[0.25em] text-center text-bone">CONTROLLA LA TUA EMAIL</h1>
        <p data-testid="reset-request-sent" className="text-bone/60 text-sm text-center leading-relaxed">
          Se l'email è registrata, riceverai a breve il link per reimpostare la password. Il link è valido 30
          minuti.
        </p>
        <a href="/admin" className="text-bone/40 hover:text-gold text-xs text-center tracking-widest transition-colors">
          TORNA AL LOGIN
        </a>
      </Card>
    );
  }

  return (
    <Card>
      <h1 className="font-display text-2xl tracking-[0.25em] text-center text-bone">PASSWORD DIMENTICATA</h1>
      <p className="text-bone/50 text-sm text-center">Inserisci la tua email: ti manderemo il link per reimpostare la password.</p>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <input
          data-testid="forgot-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className={inputClass}
        />
        {error && (
          <p data-testid="forgot-error" className="text-red-400 text-sm">
            {error}
          </p>
        )}
        <button
          data-testid="forgot-submit-button"
          type="submit"
          disabled={busy}
          className="bg-gold text-ink font-display tracking-[0.2em] text-lg py-3 hover:bg-gold-dark transition-colors disabled:opacity-50"
        >
          {busy ? "INVIO..." : "INVIA LINK DI RESET"}
        </button>
      </form>
      <a href="/admin" className="text-bone/40 hover:text-gold text-xs text-center tracking-widest transition-colors">
        TORNA AL LOGIN
      </a>
    </Card>
  );
}

function NewPassword({ token }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError("La password deve avere almeno 8 caratteri");
      return;
    }
    if (password !== confirm) {
      setError("Le password non coincidono");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Card>
        <h1 className="font-display text-2xl tracking-[0.25em] text-center text-bone">PASSWORD AGGIORNATA</h1>
        <p data-testid="reset-success" className="text-bone/60 text-sm text-center">
          La tua nuova password è attiva. Ora puoi accedere all'area riservata.
        </p>
        <a
          data-testid="goto-login-link"
          href="/admin"
          className="bg-gold text-ink font-display tracking-[0.2em] text-lg py-3 text-center hover:bg-gold-dark transition-colors"
        >
          VAI AL LOGIN
        </a>
      </Card>
    );
  }

  return (
    <Card>
      <h1 className="font-display text-2xl tracking-[0.25em] text-center text-bone">NUOVA PASSWORD</h1>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <input
          data-testid="reset-password-input"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Nuova password (min. 8 caratteri)"
          className={inputClass}
        />
        <input
          data-testid="reset-confirm-input"
          type="password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Conferma nuova password"
          className={inputClass}
        />
        {error && (
          <p data-testid="reset-error" className="text-red-400 text-sm">
            {error}
          </p>
        )}
        <button
          data-testid="reset-submit-button"
          type="submit"
          disabled={busy}
          className="bg-gold text-ink font-display tracking-[0.2em] text-lg py-3 hover:bg-gold-dark transition-colors disabled:opacity-50"
        >
          {busy ? "SALVATAGGIO..." : "IMPOSTA PASSWORD"}
        </button>
      </form>
    </Card>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <NewPassword token={token} /> : <RequestReset />;
}
